import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { InquiryItem, InquiryDriverItem } from "@/types/database";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export function InquiryDriverEditView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // State to track edited values
  type DayData = {
    transport_cost: number;
    parking: { [tour: string]: number };
    entrance_adult: { [tour: string]: number | null };
    entrance_child: { [tour: string]: number | null };
    meal_cost: number;
    other_cost: number;
    note?: string;
    driver_name: string;
  };

  const [editedValues, setEditedValues] = useState<{
    [day: string]: DayData;
  }>({});
  const [driverName, setDriverName] = useState<string>("");

  // Fetch original inquiry data
  const { data: inquiry, isLoading: isInquiryLoading } = useQuery<InquiryItem>({
    queryKey: ["inquiry", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inquiry")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return data as InquiryItem;
    },
  });

  // Fetch driver-specific data if it exists
  const { data: driverData, isLoading: isDriverDataLoading } = useQuery<InquiryDriverItem | null>({
    queryKey: ["inquiry_driver", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("inquiry_driver")
        .select("*")
        .eq("inquiry_id", id)
        .maybeSingle();

      if (error) throw error;
      return data as InquiryDriverItem | null;
    },
  });

  // Initialize edited values when data is loaded
  useEffect(() => {
    if (inquiry) {
      const initialValues: typeof editedValues = {};
      
      // If driver data exists, use that as the initial values
      if (driverData && driverData.tour_package_json) {
        setDriverName(driverData.driver_name || "");
        
        Object.entries(driverData.tour_package_json).forEach(([day, dayData]) => {
          initialValues[day] = {
            transport_cost: dayData.transport_cost || 0,
            parking: { ...(dayData.parking || {}) },
            entrance_adult: { ...(inquiry.tour_package_json[day].entrance_adult || {}) },
            entrance_child: { ...(inquiry.tour_package_json[day].entrance_child || {}) },
            meal_cost: dayData.meal_cost || 0,
            other_cost: dayData.other_cost || 0,
            note: dayData.note || '',
            driver_name: driverName,
          };
        });
      } 
      // Otherwise, use the original inquiry data
      else {
        Object.entries(inquiry.tour_package_json || {}).forEach(([day, dayData]) => {
          initialValues[day] = {
            transport_cost: dayData.transport_cost || 0,
            parking: dayData.parking || {},
            entrance_adult: dayData.entrance_adult || {},
            entrance_child: dayData.entrance_child || {},
            meal_cost: dayData.meal_cost || 0,
            other_cost: dayData.other_cost || 0,
            note: dayData.note || '',
            driver_name: driverName,
          };
        });
      }
      
      setEditedValues(initialValues);
    }
  }, [inquiry, driverData]);

  // Mutation to create or update driver data
  const upsertDriverData = useMutation({
    mutationFn: async (updatedData: Partial<InquiryDriverItem>) => {
      // Check if driver data already exists
      if (driverData) {
        // Update existing record
        const { error } = await supabase
          .from("inquiry_driver")
          .update(updatedData)
          .eq("id", driverData.id);

        if (error) throw error;
      } else {
        // Create new record
        const { error } = await supabase
          .from("inquiry_driver")
          .insert({
            ...updatedData,
            inquiry_id: id as string,
          });

        if (error) throw error;
      }
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inquiry_driver", id] });
      toast.success("Driver updates saved successfully");
      navigate(`/inquiry/${id}/driver`);
    },
    onError: (error: Error) => {
      toast.error(`Error saving changes: ${error.message}`);
    },
  });

  const formatIDR = (value: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleTransportChange = (day: string, value: string) => {
    const numValue = value === "" ? 0 : parseInt(value, 10);
    setEditedValues((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        transport_cost: numValue,
      },
    }));
  };

  const handleParkingChange = (day: string, tour: string, value: string) => {
    const numValue = value === "" ? 0 : parseInt(value, 10);
    setEditedValues((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        parking: {
          ...prev[day]?.parking,
          [tour]: numValue,
        },
      },
    }));
  };

  const handleEntranceAdultChange = (day: string, tour: string, value: string) => {
    const numValue = value === "" ? null : parseInt(value, 10);
    setEditedValues((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        entrance_adult: {
          ...prev[day]?.entrance_adult,
          [tour]: numValue,
        },
      },
    }));
  };

  const handleEntranceChildChange = (day: string, tour: string, value: string) => {
    const numValue = value === "" ? null : parseInt(value, 10);
    setEditedValues((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        entrance_child: {
          ...prev[day]?.entrance_child,
          [tour]: numValue,
        },
      },
    }));
  };

  const handleMealCostChange = (day: string, value: string) => {
    const numValue = value === "" ? 0 : parseInt(value, 10);
    setEditedValues((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        meal_cost: numValue,
      },
    }));
  };

  const handleOtherCostChange = (day: string, value: string) => {
    const numValue = value === "" ? 0 : parseInt(value, 10);
    setEditedValues((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        other_cost: numValue,
      },
    }));
  };

  const handleNoteChange = (day: string, value: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        note: value,
      },
    }));
  };

  const calculateTotal = (data: Record<string, any>) => {
    // Calculate total transport cost
    const totalTransportCost = Object.values(data).reduce(
      (sum: number, day: any) => sum + (Number(day.transport_cost) || 0),
      0
    );

    // Calculate total parking cost
    const totalParkingCost = Object.values(data).reduce((sum: number, day: any) => {
      const parkingCosts = Object.values(day.parking || {}).reduce(
        (parkingSum: number, cost: any) => parkingSum + (Number(cost) || 0),
        0
      );
      return sum + Number(parkingCosts);
    }, 0);

    return {
      totalTransportCost,
      totalParkingCost,
    };
  };

  const handleSave = () => {
    const updatedTourPackageJson: { [key: string]: any } = {};
    Object.entries(editedValues).forEach(([day, dayData]) => {
      updatedTourPackageJson[day] = {
        transport_cost: dayData.transport_cost,
        parking: dayData.parking,
        entrance_adult: dayData.entrance_adult,
        entrance_child: dayData.entrance_child,
        meal_cost: dayData.meal_cost,
        other_cost: dayData.other_cost,
        note: dayData.note,
      };
    });

    upsertDriverData.mutate({
      driver_name: driverName,
      tour_package_json: updatedTourPackageJson,
      total_transport_cost: Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.transport_cost) || 0), 0),
      total_parking_cost: Object.values(editedValues).reduce((sum: number, day) => {
        return sum + Object.values(day.parking || {}).reduce((s: number, cost: any) => s + (Number(cost) || 0), 0);
      }, 0),
      total_adult_entrance_fee: Object.values(editedValues).reduce((sum: number, day) => {
        const entranceAdultCosts = Object.values(day.entrance_adult || {}).reduce((s: number, cost: any) => 
          s + ((Number(cost) || 0) * inquiry.adult_count), 0);
        return sum + entranceAdultCosts;
      }, 0),
      total_child_entrance_fee: Object.values(editedValues).reduce((sum: number, day) => {
        const entranceChildCosts = Object.values(day.entrance_child || {}).reduce((s: number, cost: any) => 
          s + ((Number(cost) || 0) * inquiry.child_count), 0);
        return sum + entranceChildCosts;
      }, 0),
      grand_total: 
        // Transport Cost
        Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.transport_cost) || 0), 0) +
        // Parking Cost
        Object.values(editedValues).reduce((sum: number, day) => {
          return sum + Object.values(day.parking || {}).reduce((s: number, cost: any) => s + (Number(cost) || 0), 0);
        }, 0) +
        // Adult Entrance Fee
        Object.values(editedValues).reduce((sum: number, day) => {
          const entranceAdultCosts = Object.values(day.entrance_adult || {}).reduce((s: number, cost: any) => 
            s + ((Number(cost) || 0) * inquiry.adult_count), 0);
          return sum + entranceAdultCosts;
        }, 0) +
        // Child Entrance Fee
        Object.values(editedValues).reduce((sum: number, day) => {
          const entranceChildCosts = Object.values(day.entrance_child || {}).reduce((s: number, cost: any) => 
            s + ((Number(cost) || 0) * inquiry.child_count), 0);
          return sum + entranceChildCosts;
        }, 0) +
        // Meal Cost
        Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.meal_cost) || 0), 0) +
        // Other Cost
        Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.other_cost) || 0), 0) +
        // Hotel Price remains the same
        (inquiry?.total_hotel_price || 0)
    });
  };

  if (isInquiryLoading || isDriverDataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="h-8 w-8 rounded-full border-4 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  if (!inquiry) {
    return <div>Inquiry not found</div>;
  }

  return (
    <div className="container mx-auto py-2 space-y-2 max-w-[210mm]">
      <div className="flex justify-between items-center">
        <Button 
          variant="outline" 
          onClick={() => navigate(`/inquiry/${id}/driver`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to View
        </Button>
        <h1 className="text-xl font-bold">Driver Edit Mode</h1>
        <Button 
          onClick={handleSave}
          disabled={upsertDriverData.isPending}
        >
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>

      <Card className="shadow-sm p-4 bg-primary/5">
        <CardContent className="p-0 flex justify-center">
          <h2 className="text-3xl font-bold text-primary">Voucher Code: {inquiry.voucher_code || 'N/A'}</h2>
        </CardContent>
      </Card>

      <Card className="shadow-sm p-1">
        <CardHeader className="p-1">
          <CardTitle className="text-sm font-medium">Driver Name</CardTitle>
        </CardHeader>
        <CardContent className="p-1">
          <Input
            value={driverName}
            onChange={(e) => setDriverName(e.target.value)}
            placeholder="Enter driver name..."
            className="w-full"
          />
        </CardContent>
      </Card>

      <Card className="shadow-sm p-1">
        <CardHeader className="p-1">
          <CardTitle className="text-sm font-medium">Guest Information</CardTitle>
        </CardHeader>
        <CardContent className="p-1">
          <Table className="border-collapse border border-gray-800">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Name</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Guest</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Days</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Car Type</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="hover:bg-transparent">
                <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.name}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800">
                  {inquiry.adult_count} Adult & {inquiry.child_count} Child
                </TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.nights}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800">{inquiry.car_type || 'N/A'}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800">
                  {inquiry.start_date ? format(new Date(inquiry.start_date), "dd MMM yyyy") : 'N/A'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Tour Package Information - Editable */}
      <Card className="shadow-sm p-1">
        <CardHeader className="p-1">
          <CardTitle className="text-sm font-medium">Tour Package Information (Editable)</CardTitle>
        </CardHeader>
        <CardContent className="p-1">
          <div className="space-y-2">
            {Object.entries(editedValues).map(([day, data]) => (
              <div key={day} className="space-y-1">
                <h3 className="text-xs font-medium">{`Day ${day.replace('day_', '')}`}</h3>
                <div className="border rounded-md">
                  <Table className="border-collapse border border-gray-800">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="h-4 p-2 text-xs border border-gray-800">Tours</TableHead>
                        <TableHead className="h-4 p-2 text-xs border border-gray-800">Entrance Adult Cost</TableHead>
                        {inquiry.child_count > 0 && (
                          <TableHead className="h-4 p-2 text-xs border border-gray-800">Entrance Child Cost</TableHead>
                        )}
                        <TableHead className="h-4 p-2 text-xs border border-gray-800">Parking Cost</TableHead>
                        <TableHead className="h-4 p-2 text-xs border border-gray-800">Transport Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(inquiry.tour_package_json || {})[day]?.tours.map((tour: string, index: number) => (
                        <TableRow key={index} className="hover:bg-transparent">
                          <TableCell className="h-4 p-2 text-xs border border-gray-800">{tour}</TableCell>
                          <TableCell className="h-4 p-2 text-xs border border-gray-800">
                            <Input
                              type="number"
                              className="h-6 text-xs"
                              value={editedValues[day]?.entrance_adult[tour] || ''}
                              onChange={(e) => handleEntranceAdultChange(day, tour, e.target.value)}
                            />
                          </TableCell>
                          {inquiry.child_count > 0 && (
                            <TableCell className="h-4 p-2 text-xs border border-gray-800">
                              <Input
                                type="number"
                                className="h-6 text-xs"
                                value={editedValues[day]?.entrance_child[tour] || ''}
                                onChange={(e) => handleEntranceChildChange(day, tour, e.target.value)}
                              />
                            </TableCell>
                          )}
                          <TableCell className="h-4 p-2 text-xs border border-gray-800">
                            <Input
                              type="number"
                              className="h-6 text-xs"
                              value={editedValues[day]?.parking[tour] || 0}
                              onChange={(e) => handleParkingChange(day, tour, e.target.value)}
                            />
                          </TableCell>
                          <TableCell className="h-4 p-2 text-xs border border-gray-800">
                            {index === 0 ? (
                              <Input
                                type="number"
                                className="h-6 text-xs"
                                value={editedValues[day]?.transport_cost || 0}
                                onChange={(e) => handleTransportChange(day, e.target.value)}
                              />
                            ) : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Day Notes */}
                <div className="mt-4">
                  <label className="text-xs font-medium">Day Notes</label>
                  <textarea
                    value={editedValues[day]?.note || ''}
                    onChange={(e) => handleNoteChange(day, e.target.value)}
                    className="w-full min-h-[100px] p-2 text-xs border border-gray-800 rounded-md mt-1"
                    placeholder="Add notes for this day..."
                  />
                </div>
                {/* Meal Cost */}
                <TableRow className="h-4 hover:bg-transparent">
                  <TableCell className="p-2 text-xs">Meal Cost</TableCell>
                  <TableCell className="p-2 text-xs text-right">
                    <Input
                      type="number"
                      value={editedValues[day]?.meal_cost || 0}
                      onChange={(e) => handleMealCostChange(day, e.target.value)}
                      className="w-32 text-right text-xs"
                    />
                  </TableCell>
                </TableRow>
                {/* Other Cost */}
                <TableRow className="h-4 hover:bg-transparent">
                  <TableCell className="p-2 text-xs">Other Cost</TableCell>
                  <TableCell className="p-2 text-xs text-right">
                    <Input
                      type="number"
                      value={editedValues[day]?.other_cost || 0}
                      onChange={(e) => handleOtherCostChange(day, e.target.value)}
                      className="w-32 text-right text-xs"
                    />
                  </TableCell>
                </TableRow>
                {/* Parking Costs */}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cost Summary - Updated based on edits */}
      <Card className="shadow-sm p-1">
        <CardHeader className="p-1">
          <CardTitle className="text-sm font-medium">Updated Cost Summary</CardTitle>
        </CardHeader>
        <CardContent className="p-1">
          <Table className="border-collapse border border-gray-800">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-4 p-2 text-xs border border-gray-800">Description</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800 text-right">Original Amount</TableHead>
                <TableHead className="h-4 p-2 text-xs border border-gray-800 text-right">Updated Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="hover:bg-transparent">
                <TableCell className="h-4 p-2 text-xs border border-gray-800">Transport Cost</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_transport_cost || 0)}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">
                  {formatIDR(
                    Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.transport_cost) || 0), 0)
                  )}
                </TableCell>
              </TableRow>

              <TableRow className="hover:bg-transparent">
                <TableCell className="h-4 p-2 text-xs border border-gray-800">Parking Cost</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_parking_cost || 0)}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">
                  {formatIDR(
                    Object.values(editedValues).reduce((sum: number, day) => {
                      return sum + Object.values(day.parking || {}).reduce((s: number, cost: any) => s + (Number(cost) || 0), 0);
                    }, 0)
                  )}
                </TableCell>
              </TableRow>
              <TableRow className="hover:bg-transparent">
                <TableCell className="h-4 p-2 text-xs border border-gray-800">Entrance Adult Cost</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_adult_entrance_fee || 0)}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">
                  {formatIDR(
                    Object.values(editedValues).reduce((sum: number, day) => {
                      const entranceAdultCosts = Object.values(day.entrance_adult || {}).reduce((s: number, cost: any) => 
                        s + ((Number(cost) || 0) * inquiry.adult_count), 0);
                      return sum + entranceAdultCosts;
                    }, 0)
                  )}
                </TableCell>
              </TableRow>
              {inquiry.child_count > 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell className="h-4 p-2 text-xs border border-gray-800">Entrance Child Cost</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_child_entrance_fee || 0)}</TableCell>
                  <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">
                    {formatIDR(
                      Object.values(editedValues).reduce((sum: number, day) => {
                        const entranceChildCosts = Object.values(day.entrance_child || {}).reduce((s: number, cost: any) => 
                          s + ((Number(cost) || 0) * inquiry.child_count), 0);
                        return sum + entranceChildCosts;
                      }, 0)
                    )}
                  </TableCell>
                </TableRow>
              )}
              <TableRow className="hover:bg-transparent">
                <TableCell className="h-4 p-2 text-xs border border-gray-800">Meal Cost</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_meal_cost || 0)}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">
                  {formatIDR(
                    Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.meal_cost) || 0), 0)
                  )}
                </TableCell>
              </TableRow>
              <TableRow className="hover:bg-transparent">
                <TableCell className="h-4 p-2 text-xs border border-gray-800">Other Cost</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">{formatIDR(inquiry.total_other_cost || 0)}</TableCell>
                <TableCell className="h-4 p-2 text-xs border border-gray-800 text-right">
                  {formatIDR(
                    Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.other_cost) || 0), 0)
                  )}
                </TableCell>
              </TableRow>
              <TableRow className="hover:bg-transparent bg-gray-50">
                <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800">Grand Total</TableCell>
                <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800 text-right">{formatIDR(inquiry.grand_total || 0)}</TableCell>
                <TableCell className="h-4 p-2 text-xs font-medium border border-gray-800 text-right">
                  {formatIDR(
                    // Transport Cost
                    Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.transport_cost) || 0), 0) +
                    // Parking Cost
                    Object.values(editedValues).reduce((sum: number, day) => {
                      return sum + Object.values(day.parking || {}).reduce((s: number, cost: any) => s + (Number(cost) || 0), 0);
                    }, 0) +
                    // Adult Entrance Fee
                    Object.values(editedValues).reduce((sum: number, day) => {
                      const entranceAdultCosts = Object.values(day.entrance_adult || {}).reduce((s: number, cost: any) => 
                        s + ((Number(cost) || 0) * inquiry.adult_count), 0);
                      return sum + entranceAdultCosts;
                    }, 0) +
                    // Child Entrance Fee
                    Object.values(editedValues).reduce((sum: number, day) => {
                      const entranceChildCosts = Object.values(day.entrance_child || {}).reduce((s: number, cost: any) => 
                        s + ((Number(cost) || 0) * inquiry.child_count), 0);
                      return sum + entranceChildCosts;
                    }, 0) +
                    // Meal Cost
                    Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.meal_cost) || 0), 0) +
                    // Other Cost
                    Object.values(editedValues).reduce((sum: number, day) => sum + (Number(day.other_cost) || 0), 0) +
                    // Hotel Price remains the same
                    (inquiry?.total_hotel_price || 0)
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 mt-8 sticky bottom-0 w-full bg-white border-t shadow-lg p-4">
        <Button 
          variant="outline" 
          onClick={() => navigate(`/inquiry/${id}/driver`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={upsertDriverData.isPending}
        >
          <Save className="w-4 h-4 mr-2" />
          Save Changes
        </Button>
      </div>
    </div>
  );
}
